import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { CheckCircle, Package, Loader2 } from 'lucide-react';
import { useCart } from '../context/CartContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CheckoutSuccess = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('checking');
  const [attempts, setAttempts] = useState(0);
  const { fetchCart, clearCart } = useCart();

  useEffect(() => {
    const checkPaymentStatus = async () => {
      if (!sessionId || attempts >= 5) {
        setStatus(attempts >= 5 ? 'timeout' : 'error');
        return;
      }

      try {
        const response = await axios.get(`${API}/orders/checkout/status/${sessionId}`);
        if (response.data.payment_status === 'paid') {
          setStatus('success');
          clearCart(); // Clear cart after successful payment
        } else if (response.data.status === 'expired') {
          setStatus('expired');
        } else {
          // Continue polling
          setTimeout(() => setAttempts((prev) => prev + 1), 2000);
        }
      } catch (error) {
        console.error('Failed to check payment status:', error);
        setStatus('error');
      }
    };

    checkPaymentStatus();
  }, [sessionId, attempts, clearCart]);

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4 py-12" data-testid="checkout-success-page">
      <Card className="w-full max-w-md text-center">
        <CardContent className="pt-8 pb-8">
          {status === 'checking' && (
            <>
              <Loader2 className="h-16 w-16 text-primary mx-auto mb-4 animate-spin" />
              <h1 className="font-serif text-2xl font-bold text-foreground mb-2">Processing Payment</h1>
              <p className="text-muted-foreground">Please wait while we confirm your payment...</p>
            </>
          )}

          {status === 'success' && (
            <>
              <CheckCircle className="h-16 w-16 text-secondary mx-auto mb-4" />
              <h1 className="font-serif text-2xl font-bold text-foreground mb-2" data-testid="success-message">
                Payment Successful!
              </h1>
              <p className="text-muted-foreground mb-6">
                Thank you for your order. You will receive a confirmation email shortly.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Link to="/orders">
                  <Button className="btn-primary" data-testid="view-orders-btn">
                    <Package className="mr-2 h-4 w-4" />
                    View Orders
                  </Button>
                </Link>
                <Link to="/products">
                  <Button variant="outline">Continue Shopping</Button>
                </Link>
              </div>
            </>
          )}

          {status === 'expired' && (
            <>
              <div className="h-16 w-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-3xl">⏰</span>
              </div>
              <h1 className="font-serif text-2xl font-bold text-foreground mb-2">Session Expired</h1>
              <p className="text-muted-foreground mb-6">
                Your payment session has expired. Please try again.
              </p>
              <Link to="/cart">
                <Button className="btn-primary">Return to Cart</Button>
              </Link>
            </>
          )}

          {(status === 'error' || status === 'timeout') && (
            <>
              <div className="h-16 w-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-3xl">❓</span>
              </div>
              <h1 className="font-serif text-2xl font-bold text-foreground mb-2">Payment Status Unknown</h1>
              <p className="text-muted-foreground mb-6">
                We couldn&apos;t verify your payment status. Please check your orders or contact support.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Link to="/orders">
                  <Button className="btn-primary">Check Orders</Button>
                </Link>
                <Link to="/">
                  <Button variant="outline">Go Home</Button>
                </Link>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default CheckoutSuccess;
